import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setReportTableLeaveRights } from "../../slices/reportSlice";
import * as services from "../../services/reportApi";
import * as masterDataServices from "features/masterData/services/optionsApi";
import { Alert } from "components/elements";

function useLeaveRightsReport() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const reportTableLeaveRights = useSelector((state) => state.report.reportTableLeaveRights || []);
    const [search, setSearch] = useState({});

    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [leaveGroupOptions, setLeaveGroupOptions] = useState([]);

    const [showReport, setShowReport] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
    const [isModalIndividualOpen, setIsIndividualModalOpen] = useState(false);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [pagination, setPagination] = useState({ current: 0, page: 0, size: 10 });

    const [isDownload, setIsDownload] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const showModalDetail = (record) => {
        setSelectedRecord(record);
        setIsModalDetailOpen(true);
    };

    const showIndividualModal = (record) => {
        setSelectedRecord(record);
        setIsIndividualModalOpen(true);
    };

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, departmentListOption]);

    const modifiedLeaveGroupOptions = useMemo(() => {
        return leaveGroupOptions?.map((opt) => ({
            ...opt,
            unlimited: opt?.unlimited ? opt?.unlimited : 'N',
            emergencyFlag: opt?.emergencyFlag ? opt?.emergencyFlag : 'N',
            emergencyUsed: opt?.emergencyUsed ? opt?.emergencyUsed : 0,
            remainingLeaveDays: opt?.remainingLeaveDays,
            remainingLeaveHour: opt?.remainingLeaveHour,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
            value: opt?.id,
        }))?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, leaveGroupOptions]);

    const getLeaveGroupOptions = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                };
                const response = await services.getLeaveTypeOptions(values);
                setLeaveGroupOptions(response.data);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const getReportTableLeaveRights = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getReportTableLeaveRights({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    sortBy: params.sortBy

                });

                console.log("Response data jaaa", response?.data?.page);
                dispatch(setReportTableLeaveRights(response?.data?.page?.content));
                setPagination({
                    ...pagination,
                    // ...params?.request,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, reportTableLeaveRights, pagination]
    );

    const exportLeaveQuotaReport = useCallback(
        async (params = {}) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportQuotaLeaveReportReportPaging({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "LeaveRightsReport.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportLeaveRightsReportByEmployee = useCallback(
        async (params = {}) => {
            console.log("params", search);
            try {
                dispatch(setIsLoading(true));
                services.ExportLeaveRightsReportByEmployee({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy
                }).then(res => {
                    const url = window.URL.createObjectURL(res?.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', "LeaveRightsReportByEmployee.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, search]
    );

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {
            console.log('table', tablePagination);

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getReportTableLeaveRights({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getReportTableLeaveRights, pagination]
    );

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})
        setSearch({ ...values, year: values?.year?.format("YYYY") })

        getReportTableLeaveRights({ ...values, year: values?.year?.format("YYYY"), pagination: { page: 0, size: 10 } })
    };

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
            getLeaveGroupOptions();
        }
    }, [currentCompany, getPosition, getDepartment, getLeaveGroupOptions])

    return {
        handleSearch,
        setIsModalOpen,
        setIsModalDetailOpen,
        setIsIndividualModalOpen,
        showModal,
        showModalDetail,
        showIndividualModal,
        exportLeaveQuotaReport,
        exportLeaveRightsReportByEmployee,
        onChange,
        selectedRecord,
        showReport,
        positionOptions: modifiedPositionOptions,
        departmentOptions: modifiedDepartmentOption,
        leaveGroupOptions: modifiedLeaveGroupOptions,
        isModalOpen,
        isModalDetailOpen,
        isModalIndividualOpen,
        reportTableLeaveRights,
        pagination,
        isDownload
    }
}

export default useLeaveRightsReport;